import type { SupabaseClient } from "@supabase/supabase-js";

import { mapAuditRow, type AuditRow } from "./audit";
import type { MemberRow } from "./members";
import { MATRIX_ROLE_LABELS } from "./role-permission-matrix";

// PRD-001AE AE5: Permissions Audit Log, console read layer.
//
// THIS IS NOT A SECOND AUDIT STORE. It is a filtered read of `public.management_audit_feed`, the
// exact view the account-wide Audit section on this same page already reads. The only difference is
// the `event_type` filter, and it is applied SERVER-side on purpose: permission changes are rare
// next to scan traffic, so filtering the last 50 unfiltered rows in the browser would routinely
// show "nothing here" for a workspace whose permissions changed last week.
//
// Read-only. There is no write path in this module and there must never be one: the matrix is
// edited on the Group tab, roles on the Individual tab, and an audit trail an operator can edit is
// not an audit trail.
//
// WHAT CHANGED, as of migration 20260814120000. The feed now carries six bounded, type-checked
// detail columns projected out of `audit_events.metadata` for exactly these two event types. Raw
// `metadata` and `actor_user_id` are still not selected by the view and are still not reachable
// here. This module re-checks every projected value against the same shapes the host enforces
// before it renders one, because the surface's whole worth is that it never says more than it can
// stand behind, and a read model that loosens later must not quietly widen what this panel prints.

/** AE2/AE3 write: a role gained or lost one permission key. */
export const ROLE_PERMISSION_EVENT = "account.role_permission.updated";
/** AE4 (and the Members directory) write: a membership moved to another role. */
export const MEMBER_ROLE_EVENT = "account_member.role.updated";

/**
 * The events this tab exists to show. Both are seeded `admin_action` / `elevated` in
 * `audit_event_taxonomy`, and both are raised by the RPCs AE2 through AE4 wired.
 *
 * Everything else in the account trail stays on the account-wide Audit section below this one. A
 * key added to either write path must be added here or it will not appear.
 */
export const PERMISSION_AUDIT_EVENT_TYPES = [ROLE_PERMISSION_EVENT, MEMBER_ROLE_EVENT] as const;

/**
 * Not a live rail. The filter above is the most expensive read in this Admin section, because
 * `audit_events` is indexed on (account_id, created_at desc) and the event_type predicate is
 * applied as the index is walked. It is read on mount and on an explicit Refresh, never polled.
 */
export const PERMISSION_AUDIT_LIMIT = 50;

export const PERMISSION_AUDIT_VIEW = "management_audit_feed";

export const PERMISSION_AUDIT_UNAVAILABLE =
  "The account audit trail is not available on this server yet. Permission changes are still recorded; this console cannot read them here.";

export const PERMISSION_AUDIT_FAILED =
  "Could not read the permission history. Nothing is claimed about what has or has not changed. Check your connection and try again.";

/**
 * Codes that mean the VIEW is not deployed here, as opposed to the read failing.
 *
 * Deliberately narrower than the sibling checks in `alert-channel-policy.ts` and
 * `packages/shared/src/feed.ts`: those cover missing FUNCTIONS (42883, PGRST202) because they call
 * RPCs. This path only ever selects from a view, so a missing-function code arriving here would be
 * a surprise worth surfacing as a failure rather than quietly reading as "not deployed".
 */
const MISSING_VIEW_CODES: ReadonlySet<string> = new Set(["42P01", "PGRST205"]);

export function isMissingAuditView(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" && MISSING_VIEW_CODES.has(code);
}

// --- the bounded detail projection ---------------------------------------------------------------

/**
 * A feed row plus the six detail columns migration 20260814120000 projects.
 *
 * Every one of them is null unless the row's own event type writes it AND the value survived both
 * the host's bound and this module's. Nothing here is ever assembled from a value the console did
 * not recognise.
 */
export interface PermissionAuditRow extends AuditRow {
  /** `account.role_permission.updated` only: the key whose grant moved. */
  readonly permissionKey: string | null;
  /** `account.role_permission.updated` only: the role whose grant moved. */
  readonly permissionRole: string | null;
  readonly permissionPreviouslyGranted: boolean | null;
  readonly permissionGranted: boolean | null;
  /** `account_member.role.updated` only. */
  readonly memberPreviousRole: string | null;
  readonly memberNewRole: string | null;
}

/**
 * The permission key shape, identical to the CHECK constraint on
 * `app_private.account_role_permission_overrides` and to the host-side reader in the same
 * migration. Lowercase, dotted, no whitespace, so a projected key can never carry prose or markup
 * onto this surface.
 */
const PROJECTED_KEY_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
const PROJECTED_KEY_MAX_LENGTH = 64;

/**
 * A membership role as the enum spells it. Deliberately a shape check rather than a list of the
 * four v1 roles: L4 plans more roles, and a console that refuses to name a role the server has
 * already written would blank the detail on exactly the rows a new role makes interesting.
 */
const PROJECTED_ROLE_PATTERN = /^[a-z][a-z_]{1,30}$/;

function projectedKey(value: unknown): string | null {
  return typeof value === "string" &&
    value.length <= PROJECTED_KEY_MAX_LENGTH &&
    PROJECTED_KEY_PATTERN.test(value)
    ? value
    : null;
}

function projectedRole(value: unknown): string | null {
  return typeof value === "string" && PROJECTED_ROLE_PATTERN.test(value) ? value : null;
}

/** Booleans only. A grant delta read in the wrong direction is worse than one not read at all. */
function projectedFlag(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

/**
 * Map one feed row, gating each detail field on the event type that writes it.
 *
 * The host already gates the same way. This repeats it because the gate is the fail-closed
 * property: if the view is ever widened, an unexpected event type must still arrive here with no
 * detail rather than borrowing another event's vocabulary.
 */
export function mapPermissionAuditRow(raw: Record<string, unknown>): PermissionAuditRow {
  const base = mapAuditRow(raw);
  const isRolePermission = base.eventType === ROLE_PERMISSION_EVENT;
  const isMemberRole = base.eventType === MEMBER_ROLE_EVENT;
  return {
    ...base,
    permissionKey: isRolePermission ? projectedKey(raw.permission_key) : null,
    permissionRole: isRolePermission ? projectedRole(raw.permission_role) : null,
    permissionPreviouslyGranted: isRolePermission
      ? projectedFlag(raw.permission_previously_granted)
      : null,
    permissionGranted: isRolePermission ? projectedFlag(raw.permission_granted) : null,
    memberPreviousRole: isMemberRole ? projectedRole(raw.member_previous_role) : null,
    memberNewRole: isMemberRole ? projectedRole(raw.member_new_role) : null,
  };
}

export type PermissionAuditLoad =
  | { readonly status: "ready"; readonly rows: readonly PermissionAuditRow[] }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

/**
 * Never throws, and never resolves an error into an empty list.
 *
 * That distinction is the whole fail-closed requirement of this surface: on an audit log, "the read
 * failed" and "nothing has happened" look identical if both render as an empty list, and only one
 * of them is safe to believe.
 */
export async function fetchPermissionAuditLog(
  client: SupabaseClient,
  accountId: string,
  limit: number = PERMISSION_AUDIT_LIMIT,
): Promise<PermissionAuditLoad> {
  try {
    const { data, error } = await client
      .from(PERMISSION_AUDIT_VIEW)
      .select("*")
      .eq("account_id", accountId)
      .in("event_type", [...PERMISSION_AUDIT_EVENT_TYPES])
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error !== null) {
      return isMissingAuditView(error)
        ? { status: "unavailable", reason: PERMISSION_AUDIT_UNAVAILABLE }
        : { status: "failed", reason: PERMISSION_AUDIT_FAILED };
    }
    if (!Array.isArray(data)) {
      return { status: "ready", rows: [] };
    }
    return {
      status: "ready",
      rows: data.map((row) => mapPermissionAuditRow(row as Record<string, unknown>)),
    };
  } catch {
    return { status: "failed", reason: PERMISSION_AUDIT_FAILED };
  }
}

// --- naming the people in a row -----------------------------------------------------------------

const ACTOR_PREFIX = "Actor ";
const MEMBER_PREFIX = "Member ";

/**
 * Display-only name resolution for audit rows.
 *
 * `management_audit_feed.actor_label` and `management_member_directory.member_ref` are built from
 * the SAME expression, `left(encode(digest(user_id::text, 'sha256'), 'hex'), 8)`. The shared digest
 * is a join key by design: both views gate on the identical predicate (`is_account_member(admin,
 * supervisor, support)` or platform account access or superadmin), so matching them reveals nothing
 * this viewer could not already read side by side in the Members directory, which shows the display
 * name against the same ref. No raw user id is exposed either way.
 *
 * The two views wear the digest differently, and the shapes have drifted:
 *   - `actor_label` is `'Actor ' || digest` (migration 20260611080630, current).
 *   - `member_ref` was `'Member ' || digest` in 20260609143201, and has been the BARE digest since
 *     20260617120000 replaced the view with `app_private.member_user_ref`, which returns the eight
 *     hex characters alone. 20260802120000 kept that shape.
 * Both member shapes are accepted below, because a hosted server may carry either.
 */
export interface MemberLookup {
  /** Membership id to display label, for the member whose role changed. */
  readonly byMembershipId: ReadonlyMap<string, string>;
  /**
   * Actor digest to display label.
   *
   * A digest held by more than one membership is OMITTED rather than guessed. Eight hex characters
   * is 32 bits, so a collision is unlikely but possible, and attributing an action to the wrong
   * person on an audit log is worse than showing the opaque label the server sent.
   */
  readonly byActorDigest: ReadonlyMap<string, string>;
}

export const EMPTY_MEMBER_LOOKUP: MemberLookup = {
  byMembershipId: new Map(),
  byActorDigest: new Map(),
};

function memberLabel(member: MemberRow): string {
  return member.memberDisplayName ?? member.memberRef ?? "Member";
}

/** Eight lowercase hex characters, the exact output of `app_private.member_user_ref`. */
const DIGEST_PATTERN = /^[0-9a-f]{8}$/;

/**
 * Pull the digest out of a `member_ref`, accepting both the bare and the legacy prefixed shape.
 *
 * Anything that does not end up as eight hex characters is not a digest and is ignored, so a
 * display-name-shaped ref can never be indexed as one.
 */
function memberDigest(member: MemberRow): string | null {
  const ref = member.memberRef;
  if (ref === null) return null;
  const candidate = (ref.startsWith(MEMBER_PREFIX) ? ref.slice(MEMBER_PREFIX.length) : ref).trim();
  return DIGEST_PATTERN.test(candidate) ? candidate : null;
}

export function buildMemberLookup(members: readonly MemberRow[]): MemberLookup {
  const byMembershipId = new Map<string, string>();
  const digestCounts = new Map<string, number>();
  const digestLabels = new Map<string, string>();

  for (const member of members) {
    if (member.membershipId.length > 0) {
      byMembershipId.set(member.membershipId, memberLabel(member));
    }
    const digest = memberDigest(member);
    if (digest === null) continue;
    digestCounts.set(digest, (digestCounts.get(digest) ?? 0) + 1);
    digestLabels.set(digest, memberLabel(member));
  }

  const byActorDigest = new Map<string, string>();
  for (const [digest, count] of digestCounts) {
    if (count === 1) {
      byActorDigest.set(digest, digestLabels.get(digest)!);
    }
  }
  return { byMembershipId, byActorDigest };
}

/**
 * Who made the change, named when this workspace can name them.
 *
 * A null actor is the host's own doing (a trigger or a service path) and reads as "System". An
 * actor with no matching membership keeps the server's opaque label: that is the platform support
 * and superadmin case, and inventing a tenant name for them would be a lie.
 */
export function resolveActor(actorLabel: string | null, lookup: MemberLookup): string {
  if (actorLabel === null) return "System";
  if (!actorLabel.startsWith(ACTOR_PREFIX)) return actorLabel;
  const digest = actorLabel.slice(ACTOR_PREFIX.length).trim();
  return lookup.byActorDigest.get(digest) ?? actorLabel;
}

// --- presentation --------------------------------------------------------------------------------

export type PermissionAuditKind = "role-permission" | "member-role" | "other";

export interface PermissionAuditEntry {
  readonly auditEventId: string;
  readonly kind: PermissionAuditKind;
  /** What happened, in officer-facing words. Always one of PERMISSION_AUDIT_ALL_TITLES. */
  readonly title: string;
  /** Which key, which role, which way. Null when the event recorded nothing this console accepts. */
  readonly detail: string | null;
  /** Who did it. Never a raw user id. */
  readonly actor: string;
  /** Whose role moved, when the directory can name them. Null on every other kind. */
  readonly subject: string | null;
  readonly createdAt: string | null;
}

export const PERMISSION_AUDIT_TITLES: Readonly<Record<PermissionAuditKind, string>> = {
  "role-permission": "Role permission changed",
  "member-role": "Member role changed",
  other: "Account change",
};

/** Used when the projection told us which way the grant moved. */
export const ROLE_PERMISSION_GRANTED_TITLE = "Role permission granted";
export const ROLE_PERMISSION_REMOVED_TITLE = "Role permission removed";

/**
 * Every title this panel can render, and there must never be a sixth built from server text.
 *
 * The sibling account-wide `AuditFeed` renders `humanizeLabel(eventType)`, so echoing the server's
 * own vocabulary onto a permissions surface is a live risk rather than a hypothetical one
 * (AC-001AE.5). The detail line below names a key and a role, both shape-checked, and never an
 * event type.
 */
export const PERMISSION_AUDIT_ALL_TITLES = [
  PERMISSION_AUDIT_TITLES["role-permission"],
  ROLE_PERMISSION_GRANTED_TITLE,
  ROLE_PERMISSION_REMOVED_TITLE,
  PERMISSION_AUDIT_TITLES["member-role"],
  PERMISSION_AUDIT_TITLES.other,
] as const;

/**
 * WHAT THIS LOG STILL CANNOT SAY, stated on the surface rather than left for the operator to
 * assume.
 *
 * Before migration 20260814120000 this note said the view withheld metadata entirely, which was
 * true and is no longer. The feed now projects the key, the role, and both sides of each delta,
 * bounded and type checked. What remains is narrower and is what this sentence now carries: a row
 * whose recorded detail is absent, or arrives in a shape the host or this console refuses, renders
 * without detail rather than with a guess.
 */
export const PERMISSION_AUDIT_DETAIL_LIMIT_NOTE =
  "Each row names what changed when the change recorded it: the permission key and the role for a permission change, and both roles for a member move. A change whose recorded detail is missing, or arrives in a shape this console does not accept, is shown without it rather than guessed at.";

export function permissionAuditKind(eventType: string): PermissionAuditKind {
  if (eventType === ROLE_PERMISSION_EVENT) return "role-permission";
  if (eventType === MEMBER_ROLE_EVENT) return "member-role";
  return "other";
}

/**
 * Officer-facing role name. "Mobile (guard)" is the L4 mapping the Group and Individual tabs
 * already use, reused here so one role reads the same word on all three tabs.
 *
 * A role outside the four v1 columns keeps the enum's own word. That is the server's vocabulary,
 * not invented copy, and blanking it would hide the detail on exactly the rows a future role makes
 * worth reading.
 */
function projectedRoleLabel(role: string | null | undefined): string | null {
  if (typeof role !== "string" || role.length === 0) return null;
  return (MATRIX_ROLE_LABELS as Readonly<Record<string, string | undefined>>)[role] ?? role;
}

/** Frozen title, narrowed by the grant direction when the row recorded one. */
export function permissionAuditTitle(row: PermissionAuditRow): string {
  const kind = permissionAuditKind(row.eventType);
  if (kind === "role-permission") {
    if (row.permissionGranted === true) return ROLE_PERMISSION_GRANTED_TITLE;
    if (row.permissionGranted === false) return ROLE_PERMISSION_REMOVED_TITLE;
  }
  return PERMISSION_AUDIT_TITLES[kind];
}

/**
 * The one line that answers "what changed".
 *
 * Partial detail is rendered as the part that exists rather than dropped, because "alert.view" with
 * no role still tells an operator more than silence does, and neither half is inferred from the
 * other. Nothing at all yields null and the panel renders no line.
 */
export function permissionAuditDetail(row: PermissionAuditRow): string | null {
  const kind = permissionAuditKind(row.eventType);
  if (kind === "role-permission") {
    // Read defensively rather than by type alone: a row can reach here from a caller that built it
    // by hand, and `undefined !== null` would otherwise print the word undefined onto an audit log.
    const key = typeof row.permissionKey === "string" && row.permissionKey.length > 0
      ? row.permissionKey
      : null;
    const role = projectedRoleLabel(row.permissionRole);
    if (key !== null && role !== null) return `${key} for ${role}`;
    if (key !== null) return key;
    if (role !== null) return `For ${role}`;
    return null;
  }
  if (kind === "member-role") {
    const previous = projectedRoleLabel(row.memberPreviousRole);
    const next = projectedRoleLabel(row.memberNewRole);
    if (previous !== null && next !== null) return `From ${previous} to ${next}`;
    if (next !== null) return `Now ${next}`;
    if (previous !== null) return `Was ${previous}`;
    return null;
  }
  return null;
}

/**
 * Fold rows into display entries.
 *
 * A row whose event type is not one of the two is kept and labelled generically rather than
 * dropped. Silently hiding a row on an audit surface is the one failure mode worth avoiding even
 * when it should be unreachable, and the server-side filter means it only becomes reachable if that
 * filter changes.
 */
export function toPermissionAuditEntries(
  rows: readonly PermissionAuditRow[],
  lookup: MemberLookup = EMPTY_MEMBER_LOOKUP,
): PermissionAuditEntry[] {
  return rows.map((row) => {
    const kind = permissionAuditKind(row.eventType);
    const subject =
      kind === "member-role" && row.resourceId !== null
        ? lookup.byMembershipId.get(row.resourceId) ?? null
        : null;
    return {
      auditEventId: row.auditEventId,
      kind,
      title: permissionAuditTitle(row),
      detail: permissionAuditDetail(row),
      actor: resolveActor(row.actorLabel, lookup),
      subject,
      createdAt: row.createdAt,
    };
  });
}

/** Count line above the list. Plain, and it never rounds a number up into a claim. */
export function permissionAuditSummary(entries: readonly PermissionAuditEntry[]): string {
  const permissionChanges = entries.filter((entry) => entry.kind === "role-permission").length;
  const roleChanges = entries.filter((entry) => entry.kind === "member-role").length;
  const parts: string[] = [];
  if (permissionChanges > 0) {
    parts.push(`${permissionChanges} permission ${permissionChanges === 1 ? "change" : "changes"}`);
  }
  if (roleChanges > 0) {
    parts.push(`${roleChanges} role ${roleChanges === 1 ? "change" : "changes"}`);
  }
  if (parts.length === 0) return "No changes in view.";
  return `${parts.join(", ")} in view.`;
}
